import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';

import { GenericEligibilityCheckResponse } from 'hooks/usePaywall/usePaywall.types';
import { session, SESSION_KEYS } from 'utils/storage';

import packageInfo from '../../../package.json';

import {
  CheckIfAsapOptionReqProps,
  CheckIfAsapOptionResProps,
  GetAvailableDatesReqProps,
  GetAvailableDatesResProps,
  GetCombinedAvailabilityReqProps,
  GetCombinedAvailabilityResProps,
  GetProvidersForStateReqProps,
  GetProvidersForStateResProps,
  GetProvidersReqProps,
  GetProvidersResProps,
  HoldProviderReqProps,
  HoldProviderResProps,
  ReleaseSlotsReqProps,
  ReleaseSlotsResProps
} from './scheduling.types';

export const schedulingApi = createApi({
  baseQuery: fetchBaseQuery({
    baseUrl: import.meta.env.VITE_BASE_URL,
    credentials: 'include',
    prepareHeaders: (headers, { endpoint }) => {
      const ENDPOINTS_TO_COLLECT_DATA = [
        'getProviders',
        'checkIfAsapOptionAvailable',
        'holdProvider'
      ];
      headers.set('API-KEY', import.meta.env.VITE_API_KEY ?? '');
      headers.set('App-Version', packageInfo.version);
      headers.set('Platform', 'web-patient');
      const onboardingSession = session.get(SESSION_KEYS.ONBOARDING_ID);
      if (onboardingSession && ENDPOINTS_TO_COLLECT_DATA.includes(endpoint)) {
        headers.set('onboarding-id', onboardingSession);
      }
      return headers;
    }
  }),
  endpoints: (build) => ({
    checkForApptAuthEligibility: build.mutation<
      GenericEligibilityCheckResponse,
      { appointmentTypeCode: string }
    >({
      query: (body) => ({
        body,
        method: 'POST',
        url: '/auth/appointments/eligibility'
      })
    }),
    checkIfAsapOptionAvailable: build.query<CheckIfAsapOptionResProps, CheckIfAsapOptionReqProps>({
      query: (params) => ({
        params,
        url: '/auth/queue-availability'
      })
    }),
    getAvailableDates: build.query<GetAvailableDatesResProps, GetAvailableDatesReqProps>({
      query: ({ accessToken, ...params }) => ({
        headers: {
          ...(accessToken && { Authorization: accessToken })
        },
        params,
        url: '/auth/available-dates'
      })
    }),
    getGuestCombinedAvailability: build.query<
      GetCombinedAvailabilityResProps,
      GetCombinedAvailabilityReqProps
    >({
      query: ({ accessToken, sessionId, ...params }) => ({
        headers: {
          'session-id': sessionId,
          ...(accessToken && { Authorization: accessToken })
        },
        params,
        url: '/auth/available-time-slots'
      })
    }),
    getProviders: build.query<GetProvidersResProps, GetProvidersReqProps>({
      keepUnusedDataFor: 3,
      query: (params) => ({ params, url: '/auth/doctors' })
    }),
    getProvidersForState: build.query<GetProvidersForStateResProps, GetProvidersForStateReqProps>({
      keepUnusedDataFor: 3,
      query: (params) => ({
        params,
        url: '/auth/available-doctors'
      })
    }),
    holdProvider: build.mutation<HoldProviderResProps, HoldProviderReqProps>({
      query: ({ sessionId, ...body }) => ({
        body,
        headers: {
          'session-id': sessionId
        },
        method: 'POST',
        url: '/auth/hold-doctor'
      })
    }),
    releaseSlots: build.mutation<ReleaseSlotsResProps, ReleaseSlotsReqProps>({
      query: (sessionId) => ({
        headers: {
          'session-id': sessionId
        },
        method: 'POST',
        url: '/auth/release-slots'
      })
    })
  }),
  reducerPath: 'schedulingApi'
});

export const {
  useCheckForApptAuthEligibilityMutation,
  useGetProvidersQuery,
  useHoldProviderMutation,
  useLazyGetAvailableDatesQuery,
  useLazyGetGuestCombinedAvailabilityQuery,
  useReleaseSlotsMutation
} = schedulingApi;
